import api from '@/api/_stock-laboratoire'
import { chargerFuseauStock, dateStock, fuseauStock } from '@/utils/stock-dates'
import { CATEGORIES_LABORATOIRE, sectionsArticles } from '@/views/service-stock/stock-categories'
import { aujourdhuiLocal, enrichirLigne } from './flacons-utils'
import FlaconsEnCours from './flacons-en-cours.vue'
import FlaconsModales from './flacons-modales.vue'

// STOCK-LABO SL-2 : écran « Stock du laboratoire » — quantités transférées par la Pharmacie, seuils d'alerte,
// transferts reçus, consommations déclarées par le laboratoire (sortie de stock avec motif et auteur).
export const CODE_STOCK = 'laboratoire'

export const MOTIFS = [
    { id: 'usage_interne', libelle: 'Utilisation interne hors patient' },
    { id: 'controle_qualite', libelle: 'Contrôle qualité' },
    { id: 'perime', libelle: 'Périmé' },
    { id: 'casse', libelle: 'Casse ou perte' },
    { id: 'autre', libelle: 'Autre' }
]

const ETATS = { rupture: 'En rupture', bas: 'Sous le seuil', ok: 'Disponible' }

const message = (error, fallback) => error && error.response && error.response.data && typeof error.response.data.message === 'string' ? error.response.data.message : fallback

/** État d'une ligne : rupture (0), sous le seuil du stock, ou disponible. */
export function etatLigne(ligne) {
    const quantite = Number(ligne.quantite) || 0
    const seuil = Number(ligne.minimum) || 0
    if (quantite <= 0) return 'rupture'
    if (seuil > 0 && quantite <= seuil) return 'bas'
    return 'ok'
}

/** Famille de consommable lue dans le JSON d'attributs de l'article (clé « Famille »), sinon vide. */
export function libelleFamille(article) {
    if (!article || !article.attribut) return ''
    let attributs = article.attribut
    if (typeof attributs === 'string') {
        try { attributs = JSON.parse(attributs) } catch (e) { return '' }
    }
    return attributs && attributs.Famille ? String(attributs.Famille) : ''
}

/** Motif enregistré sur la sortie : préfixe « Laboratoire », motif choisi, commentaire éventuel. */
export function texteMotif(motif_id, commentaire) {
    const motif = MOTIFS.find((m) => m.id === motif_id)
    const texte = 'Laboratoire — ' + (motif ? motif.libelle : 'Autre')
    const complement = commentaire ? String(commentaire).trim() : ''
    return complement ? texte + ' — ' + complement : texte
}

function formulaire_vide(article_id) {
    return { article_id: article_id || null, nombre: '', motif: 'usage_interne', commentaire: '' }
}

export default {
    name: 'stock-laboratoire',
    components: { FlaconsEnCours, FlaconsModales },
    props: [],
    data() {
        return {
            stock: null, lignes: [], echanges: [], sorties: [],
            is_loading: false, erreur: '', info: '', non_configure: false,
            onglet: 'articles', recherche: '', filtre_categorie: '', filtre_etat: '',
            motifs: MOTIFS,
            modal: false, form: formulaire_vide(null), is_saving: false, erreur_modal: '',
            sequence: 0, detruit: false,
            // Réactifs suivis au flacon (04/10/2026) ; false si l'application Stock n'a pas encore la route.
            flacons_actifs: false
        }
    },

    computed: {
        fuseau_horaire() { return fuseauStock(this.$store) },
        userCredentials() {
            return this.$store.getters['authInfo']
        },

        lignes_filtrees() {
            const recherche = String(this.recherche || '').trim().toLowerCase()
            return this.lignes.filter((ligne) => {
                if (this.filtre_etat && ligne.etat !== this.filtre_etat) return false
                if (recherche && String(ligne.libelle || '').toLowerCase().indexOf(recherche) < 0) return false
                return true
            })
        },

        categories() { return CATEGORIES_LABORATOIRE },
        cartes() { return sectionsArticles(this.lignes_filtrees, this.categories)
            .filter(carte => !this.filtre_categorie || carte.id === this.filtre_categorie) },

        compteurs() {
            return {
                total: this.lignes.length,
                rupture: this.lignes.filter((l) => l.etat === 'rupture').length,
                bas: this.lignes.filter((l) => l.etat === 'bas').length,
                flacons_a_remplacer: this.lignes.reduce((total, l) => total + (l.en_cours || []).filter((f) => f.niveau === 'depasse').length, 0)
            }
        },

        echanges_recus() {
            return this.echanges.filter((echange) => Number(echange.type) === 1)
        },

        onglets() {
            return [
                { id: 'articles', nom: 'Articles', compte: this.lignes.length },
                { id: 'recus', nom: 'Reçus de la Pharmacie', compte: this.echanges_recus.length },
                { id: 'consommations', nom: 'Consommations', compte: this.sorties.length }
            ]
        },

        article_choisi() {
            const id = this.form.article_id
            return this.lignes.find((ligne) => ligne.id === id) || null
        },

        dernier_transfert() {
            return this.echanges_recus.length ? this.date_stock(this.echanges_recus[0]) : ''
        }
    },

    mounted() {
        this.charger()
    },

    beforeDestroy() {
        this.detruit = true
        this.sequence++
    },

    methods: {
        date_stock(evenement) { return dateStock(evenement, this.fuseau_horaire) },
        ouvrir_article(ligne) {
            if (!this.stock) return
            this.$router.push({ name: 'Détail du produit du service',
                params: { stockId: this.stock.id, articleId: ligne.id },
                query: { retour: 'laboratoire' } })
        },
        /** Le stock du laboratoire : par code (route STOCK-LABO), sinon dans la liste des stocks. */
        async resoudreStock() {
            try {
                const stock = await api.stockParCode(CODE_STOCK)
                if (stock && stock.id) return stock
            } catch (e) {
                // route absente ou stock inconnu : repli sur la liste
            }
            const liste = await api.stocks()
            return (Array.isArray(liste) ? liste : []).find((stock) => stock.code === CODE_STOCK) || null
        },

        async charger() {
            if (this.detruit) return
            const sequence = ++this.sequence
            this.is_loading = true
            this.erreur = ''
            this.non_configure = false
            try {
                const [stock] = await Promise.all([this.resoudreStock(), chargerFuseauStock(this.$store)])
                if (this.detruit || sequence !== this.sequence) return
                if (!stock) {
                    this.non_configure = true
                    this.stock = null
                    this.lignes = []
                    this.echanges = []
                    this.sorties = []
                    return
                }
                const [detail, echanges, flacons] = await Promise.all([api.stock(stock.id), api.echanges(stock.id),
                    // Suivi au flacon indisponible (route absente) : l'écran reste en déclaration de quantités.
                    api.flacons().catch(() => null)])
                if (this.detruit || sequence !== this.sequence) return
                this.stock = detail
                this.flacons_actifs = !!(flacons && Array.isArray(flacons.articles))
                const aujourdhui = aujourdhuiLocal()
                this.lignes = (detail && Array.isArray(detail.articles) ? detail.articles : []).map((article) => enrichirLigne({
                    id: article.id,
                    libelle: article.libelle,
                    famille: libelleFamille(article),
                    type_article_id: article.type_article_id,
                    attribut: article.attribut,
                    quantite: Number(article.quantite) || 0,
                    minimum: Number(article.minimum) || 0,
                    etat: etatLigne(article)
                }, this.flacons_actifs ? flacons : null, aujourdhui))
                this.sorties = (detail && Array.isArray(detail.sorties) ? detail.sorties : []).slice().reverse()
                this.echanges = (Array.isArray(echanges) ? echanges : []).slice().reverse()
            } catch (error) {
                if (this.detruit || sequence !== this.sequence) return
                this.erreur = message(error, 'Impossible de charger le stock du laboratoire. Réessayez avec Actualiser.')
            } finally {
                if (!this.detruit && sequence === this.sequence) this.is_loading = false
            }
        },

        libelle_etat(etat) {
            return ETATS[etat] || ''
        },

        classe_etat(etat) {
            if (etat === 'rupture') return 'badge-danger'
            if (etat === 'bas') return 'badge-warning'
            return 'badge-success'
        },

        // Réactifs au flacon : fenêtres portées par FlaconsModales (ref « flacons »), puis rechargement du stock.
        ouvrir_flacon(ligne) { this.$refs.flacons.ouvrir(ligne) },
        terminer_flacon(ligne, flacon) { this.$refs.flacons.terminer(ligne, flacon) },
        regler_suivi(ligne) { this.$refs.flacons.regler(ligne) },
        basculer_cq(ligne, flacon) { return this.$refs.flacons.basculerCq(ligne, flacon) },
        apres_flacon(texte) {
            this.erreur = ''
            this.info = texte
            return this.charger()
        },

        ouvrir_declaration(ligne) {
            this.form = formulaire_vide(ligne ? ligne.id : null)
            this.erreur_modal = ''
            this.modal = true
        },

        valider() {
            const article = this.article_choisi
            if (!article) return 'Choisissez un article.'
            const saisie = String(this.form.nombre || '').trim()
            const nombre = Number(saisie)
            if (!/^\d+$/.test(saisie) || !Number.isSafeInteger(nombre) || nombre <= 0) return 'Indiquez une quantité entière supérieure à zéro.'
            if (nombre > article.quantite) return 'La quantité dépasse le disponible (' + article.quantite + ').'
            if (this.form.motif === 'autre' && !String(this.form.commentaire || '').trim()) return 'Précisez le motif dans le commentaire.'
            return ''
        },

        payload() {
            const utilisateur = this.userCredentials && this.userCredentials.user ? this.userCredentials.user : null
            return {
                stock: this.stock.id,
                article: this.form.article_id,
                nombre: Number(this.form.nombre),
                motif: texteMotif(this.form.motif, this.form.commentaire),
                user_id: utilisateur && utilisateur.id ? utilisateur.id : null
            }
        },

        async declarer() {
            if (this.is_saving) return
            const erreur = this.valider()
            if (erreur) {
                this.erreur_modal = erreur
                return
            }
            this.is_saving = true
            this.erreur_modal = ''
            const article = this.article_choisi
            const donnees = this.payload()
            try {
                const reponse = await api.sortie(donnees)
                if (!reponse || reponse.sortie !== true) throw new Error('refus')
                this.modal = false
                this.info = 'Consommation enregistrée : ' + donnees.nombre + ' × ' + article.libelle + '.'
                await this.charger()
            } catch (error) {
                this.erreur_modal = message(error, 'La consommation n\'a pas pu être enregistrée : quantité insuffisante ou application Stock indisponible.')
            } finally {
                this.is_saving = false
            }
        }
    }
}
